import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface Conversation {
  id: string;
  type: 'direct' | 'club' | 'match';
  name: string;
  avatar: string | null;
  lastMessage: string | null;
  lastMessageTime: string | null;
  unreadCount: number;
  participantId?: string;
  clubId?: string;
  matchId?: string;
  isOnline?: boolean;
}

export interface Message {
  id: string;
  content: string;
  sender_id: string;
  receiver_id: string | null;
  club_id: string | null;
  match_id: string | null;
  created_at: string;
  is_read: boolean;
  file_url?: string | null;
  file_type?: string | null;
  file_name?: string | null;
  sender?: {
    id: string;
    name: string;
    avatar: string | null;
  };
}

export function useConversations() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['conversations', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];

      // Get direct messages
      // BUG 26 FIX: Add limit to prevent timeout on heavy users
      const { data: directMessages, error: directError } = await supabase
        .from('messages')
        .select(`
          id,
          content,
          created_at,
          is_read,
          sender_id,
          receiver_id,
          sender:profiles!messages_sender_id_fkey(id, name, avatar),
          receiver:profiles!messages_receiver_id_fkey(id, name, avatar)
        `)
        .or(`sender_id.eq.${profile.id},receiver_id.eq.${profile.id}`)
        .is('club_id', null)
        .is('match_id', null)
        .order('created_at', { ascending: false })
        .limit(500);

      if (directError) throw directError;

      // Group by conversation partner
      const directConversations = new Map<string, Conversation>();
      
      for (const msg of directMessages || []) {
        const isFromMe = msg.sender_id === profile.id;
        const partnerId = isFromMe ? msg.receiver_id : msg.sender_id;
        const partner = isFromMe ? msg.receiver : msg.sender;
        
        if (!partnerId || !partner) continue;
        
        if (!directConversations.has(partnerId)) {
          const unreadCount = (directMessages || []).filter(
            m => m.sender_id === partnerId && !m.is_read
          ).length;
          
          directConversations.set(partnerId, {
            id: partnerId,
            type: 'direct',
            name: (partner as any).name || 'Người dùng',
            avatar: (partner as any).avatar,
            lastMessage: msg.content,
            lastMessageTime: msg.created_at,
            unreadCount,
            participantId: partnerId,
          });
        }
      }

      // Get club memberships with club info
      const { data: clubMemberships } = await supabase
        .from('club_members')
        .select('club_id, club:clubs(id, name, avatar)')
        .eq('profile_id', profile.id);

      const clubIds = clubMemberships?.map(m => (m.club as any)?.id).filter(Boolean) || [];
      
      // Batch fetch all club messages in ONE query (fix N+1)
      let clubMessagesMap = new Map<string, { content: string; created_at: string }>();
      let clubUnreadMap = new Map<string, number>();
      
      if (clubIds.length > 0) {
        // Get latest message for each club using a single query with DISTINCT ON
        const { data: allClubMessages } = await supabase
          .from('messages')
          .select('club_id, content, created_at, is_read, sender_id')
          .in('club_id', clubIds)
          .order('club_id')
          .order('created_at', { ascending: false });

        // Process to get latest message per club
        const seenClubs = new Set<string>();
        for (const msg of allClubMessages || []) {
          if (msg.club_id && !seenClubs.has(msg.club_id)) {
            clubMessagesMap.set(msg.club_id, { content: msg.content, created_at: msg.created_at });
            seenClubs.add(msg.club_id);
          }
          // Count unread
          if (msg.club_id && msg.sender_id !== profile.id && !msg.is_read) {
            clubUnreadMap.set(msg.club_id, (clubUnreadMap.get(msg.club_id) || 0) + 1);
          }
        }
      }

      const clubConversations: Conversation[] = [];
      for (const membership of clubMemberships || []) {
        const club = membership.club as any;
        if (!club) continue;

        const lastMsg = clubMessagesMap.get(club.id);
        clubConversations.push({
          id: `club-${club.id}`,
          type: 'club',
          name: club.name,
          avatar: club.avatar,
          lastMessage: lastMsg?.content || null,
          lastMessageTime: lastMsg?.created_at || null,
          unreadCount: clubUnreadMap.get(club.id) || 0,
          clubId: club.id,
        });
      }

      // Get match participations with match info
      const { data: matchParticipations } = await supabase
        .from('match_participants')
        .select('match_id, match:matches(id, sport, date, location)')
        .eq('profile_id', profile.id);

      const matchIds = matchParticipations?.map(p => (p.match as any)?.id).filter(Boolean) || [];

      // Batch fetch all match messages in ONE query (fix N+1)
      let matchMessagesMap = new Map<string, { content: string; created_at: string }>();
      let matchUnreadMap = new Map<string, number>();

      if (matchIds.length > 0) {
        const { data: allMatchMessages } = await supabase
          .from('messages')
          .select('match_id, content, created_at, is_read, sender_id')
          .in('match_id', matchIds)
          .order('match_id')
          .order('created_at', { ascending: false });

        // Process to get latest message per match
        const seenMatches = new Set<string>();
        for (const msg of allMatchMessages || []) {
          if (msg.match_id && !seenMatches.has(msg.match_id)) {
            matchMessagesMap.set(msg.match_id, { content: msg.content, created_at: msg.created_at });
            seenMatches.add(msg.match_id);
          }
          // Count unread
          if (msg.match_id && msg.sender_id !== profile.id && !msg.is_read) {
            matchUnreadMap.set(msg.match_id, (matchUnreadMap.get(msg.match_id) || 0) + 1);
          }
        }
      }

      const matchConversations: Conversation[] = [];
      for (const participation of matchParticipations || []) {
        const match = participation.match as any;
        if (!match) continue;

        const lastMsg = matchMessagesMap.get(match.id);
        matchConversations.push({
          id: `match-${match.id}`,
          type: 'match',
          name: `Trận ${match.date}`,
          avatar: null,
          lastMessage: lastMsg?.content || null,
          lastMessageTime: lastMsg?.created_at || null,
          unreadCount: matchUnreadMap.get(match.id) || 0,
          matchId: match.id,
        });
      }

      // Combine and sort by last message time
      const allConversations = [
        ...Array.from(directConversations.values()),
        ...clubConversations,
        ...matchConversations,
      ].sort((a, b) => {
        if (!a.lastMessageTime) return 1;
        if (!b.lastMessageTime) return -1;
        return new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime();
      });

      return allConversations;
    },
    enabled: !!profile?.id,
  });
}

export function useMessages(conversationType: 'direct' | 'club' | 'match', targetId: string) {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['messages', conversationType, targetId, profile?.id],
    queryFn: async () => {
      if (!profile?.id || !targetId) return [];

      let query = supabase
        .from('messages')
        .select(`
          id,
          content,
          sender_id,
          receiver_id,
          club_id,
          match_id,
          created_at,
          is_read,
          file_url,
          file_type,
          file_name,
          sender:profiles!messages_sender_id_fkey(id, name, avatar)
        `)
        .order('created_at', { ascending: true });

      if (conversationType === 'direct') {
        query = query
          .or(`and(sender_id.eq.${profile.id},receiver_id.eq.${targetId}),and(sender_id.eq.${targetId},receiver_id.eq.${profile.id})`)
          .is('club_id', null)
          .is('match_id', null);
      } else if (conversationType === 'club') {
        query = query.eq('club_id', targetId);
      } else if (conversationType === 'match') {
        query = query.eq('match_id', targetId);
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []).map(msg => ({
        ...msg,
        sender: msg.sender as any,
      })) as Message[];
    },
    enabled: !!profile?.id && !!targetId,
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({
      content,
      receiverId,
      clubId,
      matchId,
      fileUrl,
      fileType,
      fileName,
    }: {
      content: string;
      receiverId?: string;
      clubId?: string;
      matchId?: string;
      fileUrl?: string;
      fileType?: string;
      fileName?: string;
    }) => {
      if (!profile?.id) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('messages')
        .insert({
          content,
          sender_id: profile.id,
          receiver_id: receiverId || null,
          club_id: clubId || null,
          match_id: matchId || null,
          file_url: fileUrl || null,
          file_type: fileType || null,
          file_name: fileName || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export async function uploadChatAttachment(file: File, userId: string): Promise<{ url: string; type: string; name: string }> {
  const fileExt = file.name.split('.').pop();
  const fileName = `${userId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
  
  const { data, error } = await supabase.storage
    .from('chat-attachments')
    .upload(fileName, file);
    
  if (error) throw error;
  
  const { data: urlData } = supabase.storage
    .from('chat-attachments')
    .getPublicUrl(data.path);
    
  return {
    url: urlData.publicUrl,
    type: file.type,
    name: file.name,
  };
}

export function useMarkMessagesAsRead() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({
      conversationType,
      targetId,
    }: {
      conversationType: 'direct' | 'club' | 'match';
      targetId: string;
    }) => {
      if (!profile?.id) throw new Error('Not authenticated');

      let query = supabase
        .from('messages')
        .update({ is_read: true })
        .neq('sender_id', profile.id)
        .eq('is_read', false);

      if (conversationType === 'direct') {
        query = query.eq('sender_id', targetId).eq('receiver_id', profile.id);
      } else if (conversationType === 'club') {
        query = query.eq('club_id', targetId);
      } else if (conversationType === 'match') {
        query = query.eq('match_id', targetId);
      }

      const { error } = await query;
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });
}
